import { ref } from 'vue';
import { defineStore } from 'pinia';
import {
  matchesCalendarEventPageSlug,
  normalizeCalendarEventPageSlug,
} from '@/js/core/utils/calendar-event-route';
import { getEvent, listEvents, nextEventsPage } from '@/js/core/utils/events-api';

export const useEventsListStore = defineStore('eventsList', () => {
  const events = ref([]);
  // Identifies the request `events` currently holds, so callers can tell whether the loaded
  // records match the range they are asking about or are still the previous range's.
  const loadedRequestKey = ref('');
  const detailEvents = ref([]);
  const hydratedSlugs = ref({});
  const needsRefreshBySlug = ref({});
  const detailRefreshErrorBySlug = ref({});
  const isLoading = ref(false);
  const error = ref('');
  const inFlightRequests = new Set();
  const inFlightDetailRefreshes = new Set();
  const cachedEventsByRequest = new Map();
  let latestRequestKey = '';

  function eventIdentity(event) {
    return event?.id ?? event?.gsd_service_api_id ?? event?.event_page_slug ?? null;
  }

  function mergeDetailEvent(event) {
    if (!event) return null;

    const identity = eventIdentity(event);
    if (identity === null) {
      detailEvents.value = [...detailEvents.value, event];
      return event;
    }

    const index = detailEvents.value.findIndex(existing => eventIdentity(existing) === identity);

    if (index === -1) {
      detailEvents.value = [...detailEvents.value, event];
      return event;
    }

    detailEvents.value = detailEvents.value.map((existing, existingIndex) =>
      existingIndex === index ? { ...existing, ...event } : existing
    );

    return detailEvents.value[index];
  }

  function metadataWithSlug(metadata, slug, value) {
    return { ...metadata.value, [slug]: value };
  }

  function findEventBySlug(eventPageSlug) {
    const slug = normalizeCalendarEventPageSlug(eventPageSlug);

    if (!slug) return null;

    return (
      detailEvents.value.find(event => matchesEventPageSlug(event, slug)) ||
      events.value.find(event => matchesEventPageSlug(event, slug)) ||
      null
    );
  }

  function setEventFromHydration(event, options = {}) {
    const mergedEvent = mergeDetailEvent(event);
    const slug = normalizeCalendarEventPageSlug(
      options.matchedSlug || options.slug || event?.event_page_slug
    );

    if (slug) {
      hydratedSlugs.value = metadataWithSlug(hydratedSlugs, slug, true);
      needsRefreshBySlug.value = metadataWithSlug(
        needsRefreshBySlug,
        slug,
        options.needsDetailRefresh === true
      );
    }

    return mergedEvent;
  }

  function shouldRefreshHydratedEvent(eventPageSlug) {
    const slug = normalizeCalendarEventPageSlug(eventPageSlug);

    return Boolean(slug && hydratedSlugs.value[slug] && needsRefreshBySlug.value[slug]);
  }

  async function refreshEventDetail(eventPageSlug) {
    const slug = normalizeCalendarEventPageSlug(eventPageSlug);

    if (!slug || inFlightDetailRefreshes.has(slug)) {
      return null;
    }

    inFlightDetailRefreshes.add(slug);
    detailRefreshErrorBySlug.value = metadataWithSlug(detailRefreshErrorBySlug, slug, '');

    try {
      const response = await getEvent(slug);
      const event = response.data;

      return event ? mergeDetailEvent(event) : null;
    } catch (err) {
      console.error('Event detail refresh failed.', err);
      detailRefreshErrorBySlug.value = metadataWithSlug(
        detailRefreshErrorBySlug,
        slug,
        err.message || 'Could not refresh event details.'
      );

      return null;
    } finally {
      inFlightDetailRefreshes.delete(slug);

      if (hydratedSlugs.value[slug]) {
        needsRefreshBySlug.value = metadataWithSlug(needsRefreshBySlug, slug, false);
      }
    }
  }

  /**
   * Fetch and cache all paginated calendar events for a filter/date range.
   */
  async function fetchEvents(filters = {}, type, key) {
    const rangeKey = eventsRequestKey(filters);

    latestRequestKey = rangeKey;
    // Reset before the early returns below, otherwise a month that failed leaves the error set
    // and a later cache hit renders it in place of the events it just loaded.
    error.value = '';

    if (cachedEventsByRequest.has(rangeKey)) {
      events.value = cachedEventsByRequest.get(rangeKey);
      loadedRequestKey.value = rangeKey;
      isLoading.value = false;
      return;
    }

    if (inFlightRequests.has(rangeKey)) {
      isLoading.value = true;
      return;
    }

    isLoading.value = true;
    inFlightRequests.add(rangeKey);

    try {
      let allResults = [];
      let currentPage = 1;

      while (currentPage) {
        const params = {
          ...filters,
          per_page: 100,
          page: currentPage,
        };
        const response = await listEvents(params, type, key ? key : null);

        allResults = [...allResults, ...(response.data || [])];
        currentPage = nextEventsPage(response.meta?.pagination, currentPage);
      }

      const uniqueResults = Array.from(new Map(allResults.map(item => [item.id, item])).values());

      cachedEventsByRequest.set(rangeKey, uniqueResults);

      if (latestRequestKey === rangeKey) {
        events.value = uniqueResults;
        loadedRequestKey.value = rangeKey;
      }
    } catch (err) {
      if (latestRequestKey === rangeKey) {
        console.error('API Error:', err);
        error.value = 'Could not load events.';
      }
    } finally {
      inFlightRequests.delete(rangeKey);
      if (latestRequestKey === rangeKey) {
        isLoading.value = false;
      }
    }
  }

  /**
   * Fetch one event detail record unless an already-loaded event matches the route slug.
   */
  async function fetchEvent(eventPageSlug) {
    if (!eventPageSlug) {
      return;
    }

    const slug = normalizeCalendarEventPageSlug(eventPageSlug);
    const loadedEvent = findEventBySlug(eventPageSlug);

    if (loadedEvent) {
      mergeDetailEvent(loadedEvent);

      if (slug && !hydratedSlugs.value[slug]) {
        refreshEventDetail(slug).then(event => {
          if (event) {
            hydratedSlugs.value = metadataWithSlug(hydratedSlugs, slug, true);
          }
        });
      }

      return;
    }

    isLoading.value = true;
    error.value = '';

    try {
      const response = await getEvent(eventPageSlug);
      const event = response.data;

      if (event) {
        mergeDetailEvent(event);

        if (slug) {
          hydratedSlugs.value = metadataWithSlug(hydratedSlugs, slug, true);
        }
      }
    } catch (err) {
      console.error('API Error:', err);
      error.value = 'Could not load event details.';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    events,
    loadedRequestKey,
    detailEvents,
    hydratedSlugs,
    needsRefreshBySlug,
    detailRefreshErrorBySlug,
    isLoading,
    error,
    fetchEvents,
    fetchEvent,
    findEventBySlug,
    setEventFromHydration,
    refreshEventDetail,
    shouldRefreshHydratedEvent,
  };
});

/**
 * Build the cache/identity key for an events list request. Exported so callers can ask whether
 * the store's currently loaded events match the request they care about.
 */
export function eventsRequestKey(filters = {}) {
  const rangeKey = (filters.date_ranges || [])
    .map(dateRange => `${dateRange.start_after}_${dateRange.start_before || 'none'}`)
    .join('_');
  const planningView = filters.planning_view?.trim() || 'standard';
  const filterKey = JSON.stringify(filters.filters || {});

  return `${rangeKey}_${filterKey}_${planningView}`;
}

/**
 * Match primary and occurrence event route slugs against loaded calendar events.
 */
export function matchesEventPageSlug(event, eventPageSlug) {
  return matchesCalendarEventPageSlug(event, eventPageSlug);
}
